/*
 * Project: BookingEngine
 * Module: components/booking/rooms/CartRoom.js
 */
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { getDistributionsLabel } from 'bookingform';
import formatCurrency from 'format-currency';

import { DEVICES } from '../../../actions/Booking';
import { getPhotoThumb, getLangValue } from '../../../utils/Generic';
import { isValidAge } from '../../../utils/Room';

import BookingFormDates from '../BookingFormDates';
import BookingFormLocation from '../BookingFormLocation';
import RoomRateBoard from './RoomRateBoard';
import RoomAmounts from './RoomAmounts';
import RoomCategory from './RoomCategory';
import HotelTax from '../hotels/HotelTax';
import CartDiscount from './CartDiscount';

class CartRoom extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      roomAmounts: this.adaptRoomAmounts(props.cartRoom),
    };
  }

  adaptRoomAmounts(cartRoom) {
    const currencyAmounts = {};
    Object.keys(cartRoom.amount).forEach((currencyCode) => {
      currencyAmounts[currencyCode] = {
        amount: cartRoom.amount[currencyCode],
        amount_before: cartRoom.amount_before[currencyCode],
      };
    });
    // format { EUR: { amount: 1, amountBefore: 2 }}
    return currencyAmounts;
  }

  getPaxesLabel(cartRoom) {
    /* Adapts the format from the API to the format in BookingEngine */
    const { adults, ages } = cartRoom.use;
    const childrenQuantity = ages.filter((age) => isValidAge(this.props.cartHotel, 'children', age)).length;
    const infantsQuantity = ages.length - childrenQuantity;
    const roomUse = { adl: adults, chd: childrenQuantity, enf: infantsQuantity };
    return getDistributionsLabel([roomUse], this.context);
  }

  hasHotelCurrencyDisplay() {
    const { userCurrency } = this.props;
    const hotelCurrency = this.getHotelCurrency();
    return hotelCurrency && hotelCurrency.code !== userCurrency.code;
  }

  getHotelCurrency() {
    return this.props.cartHotel.currency;
  }

  updateExtrasPrice(extras) {
    const { roomAmounts } = this.state;
    const currencyAmounts = {};
    const priceExtra = {};
    extras.forEach((extra) => {
      Object.keys(extra.prices).forEach((currencyCode) => {
        priceExtra[currencyCode] = extra.prices[currencyCode].amount + (priceExtra[currencyCode] || 0);
        currencyAmounts[currencyCode] = {
          amount: (roomAmounts && roomAmounts[currencyCode] ? parseFloat(roomAmounts[currencyCode].amount) : 0)
          + parseFloat(priceExtra[currencyCode]),
          amount_before: (roomAmounts && roomAmounts[currencyCode] ? parseFloat(roomAmounts[currencyCode].amount_before)
            : 0) + parseFloat(priceExtra[currencyCode]),
        };
      });
    });
    // format { EUR: { amount: 1, amountBefore: 2 }}
    return currencyAmounts;
  }

  renderRoomPicture(photos) {
    const { isMobile } = this.props;
    if (isMobile) {
      return null;
    }

    const height = 400;
    const width = 500;

    if (photos.length > 0) {
      return getPhotoThumb({ url: photos[0].url, width, height });
    }
    return (
      <img src={`http://dummyimage.com/${width}x${height}/e6e6e6/e6e6e6`} width={width} height={height} />
    );
  }

  renderRoomExtra() {
    if (!this.props.isFreeModeSelected) {
      return null;
    }

    const {
      checkIn, checkOut,
    } = this.props.cartRoom;
    return (
      <React.Fragment>
        <BookingFormLocation hotelName={this.props.cartHotel.name} />
        <BookingFormDates checkIn={checkIn} checkOut={checkOut} />
      </React.Fragment>
    );
  }

  renderHeader(cartRoom) {
    const { room } = cartRoom;
    return (
      <header className="room pl-4">
        <div className="room__name">
          {getLangValue(room, this.props.lang, 'name')}
          <RoomCategory className="text-xs" category={room.category} />
        </div>
        {room.adults_only && <div className="text-primary">{this.context.t('Adults only')}</div>}
        <div className="room__paxes">
          {this.getPaxesLabel(cartRoom)}
        </div>
        {this.renderRoomExtra(cartRoom)}
      </header>
    );
  }

  renderPriceRoomToExtras(room, currency) {
    const { roomAmounts } = this.state;
    return (
      <div className='extra-cart flex justify-between'>
        <span className='flex items-center capitalize text-sm'>{getLangValue(room.room, this.props.lang, 'name')}</span>
        <span className='text-sm'>{formatCurrency(roomAmounts[currency.code].amount, currency)}</span>
      </div>
    );
  }

  renderExtras(extras, currency) {
    return (
      <div className='extra-cart'>
        <div className="extra-amounts__totalprice">{this.context.t('Extras')}:</div>
        <ul className='list-disc'>
          {extras.map((extra) => (
            <li key={extra.extra_uuid} className='flex justify-between'>
              <span className='flex items-center capitalize'>
                <i className="fa fa-circle"/> {getLangValue(extra, this.props.lang, 'name').toLowerCase()}</span>
              <span>{formatCurrency(extra.prices[currency.code].amount, currency)}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  getNewMemberDiscountPercentage() {
    //Regresa loyalty.newMemberDiscountPercentage
    //Si es 0, regresa this.props.new_member_discount_percentage ya que eso es igual a chain.Data.new_member_discount_percentage
    if (this.props.new_member_discount_percentage != null) {
      if (this.props.new_member_discount_percentage > 0) {
        return this.props.new_member_discount_percentage;
      }

      return this.props.new_member_discount_percentage_chain;
    }
    return this.props.new_member_discount_percentage_chain;
  }

  renderRoomDiscount() {
    const isSingleRoom = this.props.cartHotel.rooms.length === 1;
    const { hotel } = this.props;
    if (!hotel.booking_show_room_discounts) {
      return null;
    }
    // The discount can be calculated for any currency, it should be the same in all cases
    const { amount, amount_before } = this.state.roomAmounts[this.props.userCurrency.code];
    let additionalDiscount = 0;
    if (this.props.new_member === true
        && this.props.new_member_discount_percentage_activated === true
        && isSingleRoom === true) {
      ////TODOCANTIDADES
      //Acá se muestra cada uno de los cuartos (cuando son varios), en caso de que sea uno sólo se pidió que sí se muestre
      //el descuento adicional
      //cuando son varios no, porque eso se mostrará hasta el final en el recuadro que sumariza
      additionalDiscount = this.getNewMemberDiscountPercentage();
    }
    return (
      <CartDiscount amount={amount} amountBefore={amount_before} additionalDiscount={additionalDiscount}/>
    );
  }

  renderHotelCurrencyPrice(hotel, cartRoom, hotelCurrency) {
    const isSingleRoom = this.props.cartHotel.rooms.length === 1;
    const extraProps = {
      showTotalPrice: isSingleRoom,
      showTax: isSingleRoom,
      extraClass: isSingleRoom ? 'room-amounts--totals' : '',
    };
    if (isSingleRoom) {
      extraProps.renderSpecialBlock = () => <HotelTax
        loggedUser={!!this.props.loggedUser}
        hotel={this.props.cartHotel}
        lang={this.props.lang} />;
    }
    const currency = cartRoom.rate.is_custom_currency_rate
      ? this.props.currencies.find((element) => element.code === this.props.cart.custom_currency_code)
      : hotelCurrency;
    const { extras } = cartRoom;
    let { roomAmounts } = this.state;
    if (extras && extras.length > 0) {
      // Update total price with extras price
      roomAmounts = this.updateExtrasPrice(extras);
    }
    return (
      <div className="cartroom__Price sandbox border-primary">
        {this.renderRoomDiscount()}
        {extras && extras.length > 0 && this.renderPriceRoomToExtras(cartRoom, currency)}
        {extras && extras.length > 0 && this.renderExtras(extras, currency)}
        <RoomAmounts
          amounts={roomAmounts}
          currency={currency}
          nights={cartRoom.nights}
          showOriginal={hotel.show_original_price} {...extraProps}
          showDescription={true}
          showFinalAmount={this.props.loggedUser != null && isSingleRoom === true} //Si el usuario de loyalty está logeado y si es sólo un cuarto
          //podremos mostrar la cantidad final ya con el descuento
          ////TODOCANTIDADES
          coinsApplied={this.props.coinsApplied}
        >
        </RoomAmounts>
      </div>
    );
  }

  renderRoomPrices() {
    const { hotel, cartRoom, userCurrency } = this.props;
    if (this.hasHotelCurrencyDisplay()) {
      const hotelCurrency = this.getHotelCurrency();
      return (
        <div className="room-amounts-wrapper">
          <RoomAmounts
            amounts={this.state.roomAmounts}
            currency={userCurrency}
            nights={cartRoom.nights}
            showOriginal={hotel.show_original_price}
            extraClass="room-amounts--inner"
            showDescription={true}
            showFinalAmount={false}
            coinsApplied={this.props.coinsApplied}
          >
            {this.context.t('Selected currency amounts')}
          </RoomAmounts>
          {this.renderHotelCurrencyPrice(hotel, cartRoom, hotelCurrency)}
        </div>
      );
    }
    // User currency and hotel currency are the same
    return this.renderHotelCurrencyPrice(hotel, cartRoom, userCurrency);
  }

  render() {
    const { cartRoom, mode } = this.props;
    return (
      <div className="cartroom">
        {this.renderHeader(cartRoom)}
        <div className="cartroom__content">
          <figure className="cartroom__img">
            {this.renderRoomPicture(cartRoom.room.photos)}
          </figure>
          {mode === 'individual' && <RoomRateBoard room={cartRoom} hotel={this.props.hotel} lang={this.props.lang}
          new_member = {this.props.new_member}
          new_member_discount_percentage_activated = {this.props.new_member_discount_percentage_activated}
          loyaltyName = {this.props.loyaltyName}/>}
          {this.renderRoomPrices()}
        </div>
      </div>
    );
  }
}

CartRoom.propTypes = {
  position: PropTypes.number.isRequired,
  cartRoom: PropTypes.object,
  mode: PropTypes.oneOf(['individual', 'combined']).isRequired, // each room with all info or 2 rooms with summary info
  hotel: PropTypes.object,
  cartHotel: PropTypes.object.isRequired,
  userCurrency: PropTypes.object.isRequired,
};

CartRoom.contextTypes = {
  t: PropTypes.func.isRequired,
};

export default connect((state) => ({
  isMobile: state.booking.device === DEVICES.MOBILE,
  lang: state.i18nState.lang,
  isFreeModeSelected: state.bookingFormState.isFreeModeSelected,
  loggedUser: state.loyalty.loggedUser,
  cart: state.cart,
  currencies: state.booking.currencies,
  new_member: state.loyalty.newMember,
  new_member_discount_percentage: state.loyalty.newMemberDiscountPercentage,
  new_member_discount_percentage_chain: state.booking.hotel !== null
    ? state.session.chainData.loyalty_levels
      .find((member) => (member.membership_name === (state.bookingFormState.agentCode !== '' ? 'Blue Agent' : 'Blue')))
      .new_member_discount_percentage_hotels[state.booking.hotel.code.toLowerCase()]
    : 0,
  new_member_discount_percentage_activated: state.booking.hotel != null
    ? (state.booking.hotel.new_member_discount_percentage_activated === true
      && state.bookingFormState.agentSell === 'false') : false,
  loyaltyName: state.session.chainData.booking_engine_configuration.loyalty_name,
  coinsApplied: state.loyalty.coinsApplied,
}))(CartRoom);
